import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useMouseParallax } from '../hooks/useMouseParallax'
import { useReducedMotion } from '../hooks/useReducedMotion'
import Particles from './Particles'

const stats = [
  { label: 'Focus', value: 72 },
  { label: 'Energia', value: 45 },
  { label: 'Disciplina', value: 58 },
  { label: 'Corpo', value: 38 },
  { label: 'Stress Control', value: 61 },
]

export default function HeroSection() {
  const reduced = useReducedMotion()
  const mouse = useMouseParallax(reduced ? 0 : 30)
  const [loaded, setLoaded] = useState(false)
  const heroRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section
      ref={heroRef}
      className="relative w-full min-h-screen overflow-hidden flex items-center justify-center"
      style={{ background: '#050510' }}
    >
      {/* Deep background layer */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.12) 0%, rgba(5,5,16,1) 70%)',
          x: reduced ? 0 : mouse.x * 0.3,
          y: reduced ? 0 : mouse.y * 0.3,
        }}
      />

      {/* Grid perspective floor */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[60vh] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(139,92,246,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.1) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          transform: 'perspective(600px) rotateX(70deg)',
          transformOrigin: 'center bottom',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      />

      {/* Particles layer */}
      <Particles count={50} />

      {/* Floating orbs */}
      {!reduced && (
        <>
          <motion.div
            className="absolute w-64 h-64 rounded-full blur-[100px] pointer-events-none"
            style={{
              background: 'rgba(139,92,246,0.15)',
              top: '20%',
              left: '10%',
            }}
            animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute w-48 h-48 rounded-full blur-[80px] pointer-events-none"
            style={{
              background: 'rgba(0,212,255,0.12)',
              bottom: '30%',
              right: '15%',
            }}
            animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
        </>
      )}

      {/* HUD Stats - Left side */}
      <motion.div
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 hidden md:flex flex-col gap-3"
        initial={{ opacity: 0, x: -50 }}
        animate={loaded ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.5, staggerChildren: 0.1 }}
      >
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            className="hud-stat w-40"
            initial={{ opacity: 0, x: -30 }}
            animate={loaded ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.7 + i * 0.15 }}
          >
            <div className="flex justify-between text-[10px] text-nerd-gray mb-1">
              <span>{stat.label}</span>
              <span className="text-nerd-cyan">{stat.value}%</span>
            </div>
            <div className="hud-stat-bar">
              <motion.div
                className="hud-stat-fill"
                initial={{ width: 0 }}
                animate={loaded ? { width: `${stat.value}%` } : {}}
                transition={{ duration: 1.2, delay: 1 + i * 0.15, ease: 'easeOut' }}
              />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Central content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        {/* Decorative top line */}
        <motion.div
          className="w-16 h-[1px] bg-gradient-to-r from-transparent via-nerd-purple to-transparent mx-auto mb-8"
          initial={{ scaleX: 0 }}
          animate={loaded ? { scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
        />

        {/* Headline */}
        <motion.h1
          className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={loaded ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="block text-nerd-white">La tua vita non è finita.</span>
          <motion.span
            className="block text-gradient mt-2"
            initial={{ opacity: 0, y: 20 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Sei solo fermo al checkpoint.
          </motion.span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-nerd-gray text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Coaching mentale e fisico per nerd, gamer e persone che vogliono tornare ad agire nella vita reale.
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <a href="#percorso" className="nerd-btn-primary text-sm sm:text-base">
            Inizia la tua quest
          </a>
          <a href="#metodo" className="nerd-btn-secondary text-sm sm:text-base">
            Vedi il metodo
          </a>
        </motion.div>

        {/* Decorative bottom line */}
        <motion.div
          className="w-16 h-[1px] bg-gradient-to-r from-transparent via-nerd-blue to-transparent mx-auto mt-12"
          initial={{ scaleX: 0 }}
          animate={loaded ? { scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
        />
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ delay: 1.5 }}
      >
        <span className="text-[10px] text-nerd-gray tracking-widest uppercase font-display">Scroll</span>
        <motion.div
          className="w-[1px] h-8 bg-gradient-to-b from-nerd-purple to-transparent"
          animate={{ scaleY: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* Corner HUD decorations */}
      <div className="absolute top-4 left-4 w-8 h-8 border-l border-t border-nerd-purple/30 pointer-events-none" />
      <div className="absolute top-4 right-4 w-8 h-8 border-r border-t border-nerd-purple/30 pointer-events-none" />
      <div className="absolute bottom-4 left-4 w-8 h-8 border-l border-b border-nerd-purple/30 pointer-events-none" />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-nerd-purple/30 pointer-events-none" />
    </section>
  )
}
